// SourceInventoryBlock — every document, data feed, prior draft, or
// snapshot the composer drew on. Stale sources are badged so the reviewer
// sees at a glance which inputs were past their freshness policy.

import { AlertTriangle } from 'lucide-react';
import type { ComposerSourceInventoryShape } from '@/data/types';

interface SourceInventoryBlockProps {
    inventory: ComposerSourceInventoryShape;
}

export default function SourceInventoryBlock({ inventory }: SourceInventoryBlockProps) {
    // A draft authored without a full envelope (e.g. a seeded/legacy row where
    // `sourceInventory` is an empty object) must degrade rather than throw —
    // coerce the two arrays defensively so render never dereferences undefined.
    const sources = Array.isArray(inventory?.sources) ? inventory.sources : [];
    const staleMarkers = Array.isArray(inventory?.staleMarkers) ? inventory.staleMarkers : [];
    return (
        <div
            data-testid="workbench-source-inventory"
            className="rounded-md border border-[rgb(var(--color-divider))] bg-[rgb(var(--color-surface-secondary)/0.4)] p-3"
        >
            <div className="text-card-title text-[rgb(var(--color-text-primary))] mb-2">
                Source inventory
            </div>
            {sources.length === 0 && staleMarkers.length === 0 && (
                <p className="text-annotation text-[rgb(var(--color-text-tertiary))] italic">
                    No sources recorded on this draft.
                </p>
            )}
            <ul className="flex flex-col gap-1.5">
                {sources.map((s, i) => (
                    <li
                        key={`${s.sourceType}-${s.sourceId ?? i}`}
                        data-testid="workbench-source-item"
                        className="text-annotation text-[rgb(var(--color-text-secondary))]"
                    >
                        <span className="font-mono text-[rgb(var(--color-text-primary))]">
                            [{s.sourceType}]
                        </span>{' '}
                        {s.label}
                        {s.isStale && (
                            <span
                                data-testid="workbench-source-stale"
                                className="ml-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-button text-overline uppercase bg-[rgb(var(--color-rag-amber)/0.15)] text-[rgb(var(--color-rag-amber))]"
                            >
                                <AlertTriangle className="w-3 h-3" />
                                stale
                            </span>
                        )}
                        {s.stalenessNote && (
                            <span className="block text-[rgb(var(--color-text-tertiary))] italic mt-0.5">
                                {s.stalenessNote}
                            </span>
                        )}
                    </li>
                ))}
            </ul>
            {staleMarkers.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                    {staleMarkers.map((m) => (
                        <span
                            key={m}
                            className="text-annotation px-2 py-0.5 rounded-button bg-[rgb(var(--color-rag-amber)/0.15)] text-[rgb(var(--color-rag-amber))] font-mono"
                        >
                            {m}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}
